import React, { useCallback, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Button, ButtonProps, Chip, Stack } from "@mui/material";
import { FiltersChip } from "components";
import { useFormatDateHook } from "hooks";

import { AiDataFilters, useAiDataContext } from "../context/AiData-context";
import { FiltersDrawer } from "./FiltersDrawer";

const buttonStyles: ButtonProps["sx"] = {
  height: "26px",
  fontSize: "15px",
  textTransform: "none",
  fontWeight: "400"
};

type Field = { key: "investors" | "deals" | "measures" | "effectiveDate" | "status" | "createdAt"; title: string };

const dumbFilterTitles: Record<keyof AiDataFiltersArrs, Field> = {
  investors: { title: "Investor", key: "investors" },
  deals: { title: "Deal", key: "deals" },
  measures: { title: "Measure", key: "measures" },
  effectiveDate: { title: "Effective date", key: "effectiveDate" },
  status: { title: "Status", key: "status" },
  createdAt: { title: "Created at", key: "createdAt" }
};

type AiDataFiltersArrs = Pick<
  AiDataFilters,
  "investors" | "deals" | "measures" | "effectiveDate" | "status" | "createdAt"
>;
type OnRemoveOption = {
  field: Field;
  option:
    | {
        id: number;
        name?: string;
        title?: string;
      }
    | string;
};

export const AiDataFilter: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const { filters, updateAiDataFilters, isRecommendationsTableLoading } = useAiDataContext();

  const format = useFormatDateHook();

  const isFiltered = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ...rest } = filters;
    return Object.values(rest).some((item) => (Array.isArray(item) ? item.length : !!item));
  }, [filters]);

  const pickFilters: AiDataFiltersArrs = useMemo(() => {
    const { deals, investors, measures, status, effectiveDate, createdAt } = filters;

    return { deals, investors, measures, status, effectiveDate, createdAt };
  }, [filters]);

  const onRemoveField = useCallback(
    (field: Field) => {
      updateAiDataFilters({ ...filters, [field.key]: [] });
    },
    [filters, updateAiDataFilters]
  );

  const onRemoveOption = useCallback(
    ({ field, option }: OnRemoveOption) => {
      if (field.key === "effectiveDate" || field.key === "createdAt") {
        updateAiDataFilters({ ...filters, [field.key]: null });
        return;
      }
      updateAiDataFilters({
        ...filters,
        [field.key]: filters[field.key]?.filter((item) => {
          if (typeof item === "object" && typeof option === "object") {
            return item.id !== option.id;
          }
          return item !== option;
        })
      });
    },
    [filters, updateAiDataFilters]
  );

  const formatDateRange = (dateRange: { from?: string; to?: string } | null | undefined, prefix: string) => {
    if (!dateRange) return { from: "", to: "", divider: "" };

    const from = dateRange.from ? `${prefix}: ${format({ date: dateRange.from })}` : "";
    const to = dateRange.to ? `To: ${format({ date: dateRange.to })}` : "";
    const divider = dateRange && Object.keys(dateRange).length > 2 ? "-" : "";

    return { from, to, divider };
  };

  const effectiveDate = formatDateRange(filters.effectiveDate, "From");
  const createdAt = formatDateRange(filters.createdAt, "From");

  return (
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
      <Stack direction="row" sx={{ gap: "14px", alignItems: "center", flexWrap: "wrap" }}>
        <Button
          disabled={isRecommendationsTableLoading}
          sx={{ ...buttonStyles, backgroundColor: isFiltered ? "#dae0e2" : "transparent" }}
          onClick={() => setIsDrawerOpen(true)}
          variant="outlined"
          size="sm"
          startIcon={<FilterAltIcon />}
        >
          Filters
        </Button>
        {Object.keys(pickFilters).map((field) => {
          const filterValue = filters[field as keyof AiDataFilters];
          if (Array.isArray(filterValue)) {
            return (
              <FiltersChip
                key={field}
                field={dumbFilterTitles[field as keyof AiDataFiltersArrs]}
                options={filterValue}
                renderOption={(option) => (typeof option === "object" ? (option.name! ?? option.title) : option)}
                removeFieldOption={onRemoveOption}
                onRemove={onRemoveField}
                renderField={(field) => field.title}
              />
            );
          }
          return null;
        })}
        {filters.effectiveDate && (
          <Chip
            sx={{ height: "26px", fontSize: "15px", color: "#0a3142" }}
            label={`${effectiveDate.from} ${effectiveDate.divider} ${effectiveDate.to}`}
            onDelete={() => updateAiDataFilters({ ...filters, effectiveDate: null })}
          />
        )}
        {filters.createdAt && (
          <Chip
            sx={{ height: "26px", fontSize: "15px", color: "#0a3142" }}
            label={`${createdAt.from} ${createdAt.divider} ${createdAt.to}`}
            onDelete={() => updateAiDataFilters({ ...filters, createdAt: null })}
          />
        )}
        {isFiltered && (
          <Button
            disabled={isRecommendationsTableLoading}
            onClick={() => updateAiDataFilters(null)}
            startIcon={<CloseIcon className="icon" />}
          >
            Clear All Filters
          </Button>
        )}
      </Stack>

      <FiltersDrawer
        filters={filters}
        setFilters={updateAiDataFilters}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </Stack>
  );
};
